import { apiClient } from "@/lib/api-client";
import type {
  EventParticipantOkthxStatusResponse,
  InviteEventParticipantError,
  InviteEventParticipantRequest,
  InviteEventParticipantResponse,
} from "@/types/events.ts";
import { queryOptions, skipToken } from "@tanstack/react-query";
import { isAxiosError } from "axios";

/**
 * Which of an event's participants already use okthx or were already invited,
 * for web and native alike. Fetched apart from the event because the detail
 * screen usually serves the event from the calendar-list cache; keyed under
 * the event so invalidating the event after an edit refreshes it too.
 */
export const participantOkthxStatusQueryOptions = (eventId: string | undefined) =>
  queryOptions({
    queryKey: ["event", eventId, "participantOkthxStatus"] as const,
    queryFn: eventId
      ? ({ signal }) =>
          apiClient
            .get<EventParticipantOkthxStatusResponse>(
              `/api/a/events/${eventId}/participants/okthx-status`,
              { signal },
            )
            .then((res) => res.data)
      : skipToken,
    staleTime: 60_000,
  });

/** Invite one of the event's participants to okthx, from the user's own mailbox. */
export const inviteEventParticipant = (eventId: string, data: InviteEventParticipantRequest) =>
  apiClient
    .post<InviteEventParticipantResponse>(`/api/a/events/${eventId}/participants/invite`, data)
    .then((res) => res.data);

/**
 * How a refused invite should read. "They already use okthx" is news rather
 * than a failure, and hides the button; anything else shows the server's own
 * message, which names what to do, falling back when there is none.
 */
export function describeInviteFailure(error: unknown): {
  alreadyMember: boolean;
  message: string;
} {
  const data = isAxiosError(error)
    ? (error.response?.data as Partial<InviteEventParticipantError> | undefined)
    : undefined;
  return {
    alreadyMember: data?.code === "member",
    message: typeof data?.error === "string" ? data.error : "Couldn't send the invite",
  };
}
